import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ProgressDialogComponent } from '../ProgressDialog/progress-dialog.component';
import { ProgressStageModel } from '../../models/progress.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { SplitHeader } from '../../../TakeOver/components/TakeOverPanel/take-over-panel.component';
import { AgentsApiService } from '../../../../api/agents/agents.api';
import {
    ContextAnswer,
    ContextConversationRound,
    ContextMultiSelectQuestion,
    ContextQuestion,
} from '../../../../api/agents/models/context-takeover.api.model';
import { MultiSelectDropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

export interface QuestionState {
    chipsValue: string[];
    selectedOptions: string[];
    selectedOption: string;
    freeTextInput: string;
}

export interface QuestionHistoryEntry {
    pendingQuestions: ContextQuestion[];
    questionStates: Record<string, QuestionState>;
    refinedPurpose: string;
    conversationHistory: ContextConversationRound[];
    isComplete: boolean;
}

@Component({
    selector: 'agpr-ai-context-takeover',
    templateUrl: './ai-context-takeover.component.html',
    styleUrls: ['./ai-context-takeover.component.scss'],
    standalone: false
})
export class AIContextTakeoverComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() naturalLanguagePrompt: string = '';
    @Input() taskDescription: string = '';
    @Input() systemPrompt: string | null = null;
    @Input() overlayContainer: Element;
    @Input() protected animationTrigger: BehaviorSubject<FullProfileAnimationModel>;
    @Input() title: string = '';
    @Input() originalPurposeLabel: string = '';
    @Input() refinedPurposeLabel: string = '';
    @Input() descriptionLabel: string = 'Common.agilityAgent.descriptionLineOne';
    @Input() submitLabel: string = 'Common.prcopilot.updateSearchPurpose';
    @Input() applyLabel: string = 'Common.prcopilot.search';
    @Input() applyFn: ((refinedPurpose: string) => Observable<unknown>) | null = null;
    @Input() progressStages?: ProgressStageModel[];

    readonly buttonTypes = ButtonTypesEnum;
    readonly multiSelectDropdownThreshold = 8;
    readonly splitOnPattern = new RegExp('[,;]');
    readonly panelHeader: SplitHeader = {
        icon: 'fa-kit-duotone fa-agpr-ai-generic',
        initialText: '',
        value: '',
        endText: '',
        loading: false
    };

    hideSlideOverlay: boolean = false;
    isApplying: boolean = false;

    originalPurpose: string = '';
    refinedPurpose: string = '';
    isComplete: boolean = false;
    isLoading: boolean = false;

    pendingQuestions: ContextQuestion[] = [];
    questionStates: Record<string, QuestionState> = {};
    ignoredQuestions: Record<string, boolean> = {};
    freeTextAnswer: string = '';

    badgeStateEnum = {
        generated: 'generated',
        usingOriginalPurpose: 'usingOriginalPurpose',
        manuallyEdited: 'manuallyEdited'
    };
    badgeStateTextMap = {
        [this.badgeStateEnum.generated]: 'Common.agilityAgent.generated',
        [this.badgeStateEnum.usingOriginalPurpose]: 'Common.prcopilot.usingOriginalPurpose',
        [this.badgeStateEnum.manuallyEdited]: 'Common.prcopilot.manuallyEdited'
    };

    badgeText: string = this.badgeStateTextMap[this.badgeStateEnum.generated];

    hasAnswers: boolean = false;

    canGoBack: boolean = false;
    canGoForward: boolean = false;

    conversationHistory: ContextConversationRound[] = [];
    questionHistory: QuestionHistoryEntry[] = [];
    private historyIndex: number = -1;
    private agentSubscription: Subscription | null = null;

    constructor(
        public coreDialogService: CoreDialogService,
        private agentsApiService: AgentsApiService
    ) {
        super(coreDialogService, 'AIContextTakeover');
    }

    ngOnInit(): void {
        this.panelHeader.value = this.title;
        if (this.naturalLanguagePrompt) {
            this.originalPurpose = this.naturalLanguagePrompt;
            this.callAgent();
        }
    }

    ngOnDestroy(): void {
        this.agentSubscription?.unsubscribe();
    }

    getOptionsAsList(question: ContextQuestion): MultiSelectDropdownOptionModel[] {
        if (question.type !== 'multi_select') {
            return [];
        }
        return ((question as ContextMultiSelectQuestion).options ?? [])
            .map(opt => new MultiSelectDropdownOptionModel(opt, opt));
    }

    updateHasAnswers(): void {
        const anyQuestionAnswered = this.pendingQuestions.some(q => {
            if (this.ignoredQuestions[q.id]) {
                return false;
            }
            const state = this.questionStates[q.id];
            if (!state) {
                return false;
            }
            switch (q.type) {
                case 'text':
                    return !!state.freeTextInput.trim();
                case 'text_array':
                    return state.chipsValue.length > 0 || !!state.freeTextInput.trim();
                case 'multi_select':
                    return state.selectedOptions.length > 0 || !!state.freeTextInput.trim();
                case 'single_select':
                    return !!state.selectedOption || !!state.freeTextInput.trim();
                default:
                    return false;
            }
        });
        this.hasAnswers = anyQuestionAnswered || !!this.freeTextAnswer.trim();
    }

    ignoreQuestion(questionId: string): void {
        this.ignoredQuestions = { ...this.ignoredQuestions, [questionId]: !this.ignoredQuestions[questionId] };
        this.updateHasAnswers();
    }

    private callAgent(): void {
        this.isLoading = true;
        this.agentSubscription?.unsubscribe();
        this.agentSubscription = this.agentsApiService
            .getContext(this.naturalLanguagePrompt, this.taskDescription, this.conversationHistory, this.systemPrompt)
            .subscribe({
                next: (response) => {
                    this.isLoading = false;
                    if (response.enrichedPrompt) {
                        this.setBadgeState(this.badgeStateEnum.generated);
                        this.refinedPurpose = response.enrichedPrompt;
                    }
                    if (response.status === 'QUESTIONS' && response.questions?.length) {
                        this.pendingQuestions = response.questions;
                        this.questionStates = {};
                        this.ignoredQuestions = {};
                        for (const q of response.questions) {
                            const preselected = (q as { preselected?: string[] }).preselected ?? [];
                            this.questionStates[q.id] = {
                                chipsValue: q.type === 'text_array' ? [...preselected] : [],
                                selectedOptions: q.type === 'multi_select' ? [...preselected] : [],
                                selectedOption: '',
                                freeTextInput: '',
                            };
                        }
                        this.questionHistory = this.questionHistory.slice(0, this.historyIndex + 1);
                        this.questionHistory.push({
                            pendingQuestions: this.pendingQuestions,
                            questionStates: this.deepCopyQuestionStates(this.questionStates),
                            refinedPurpose: this.refinedPurpose,
                            conversationHistory: [...this.conversationHistory],
                            isComplete: false,
                        });
                        this.historyIndex = this.questionHistory.length - 1;
                        this.updateHistoryNavigation();
                        this.updateHasAnswers();
                    } else if (response.status === 'COMPLETE') {
                        this.isComplete = true;
                        this.pendingQuestions = [];
                        this.questionHistory = this.questionHistory.slice(0, this.historyIndex + 1);
                        this.questionHistory.push({
                            pendingQuestions: [],
                            questionStates: {},
                            refinedPurpose: this.refinedPurpose,
                            conversationHistory: [...this.conversationHistory],
                            isComplete: true,
                        });
                        this.historyIndex = this.questionHistory.length - 1;
                        this.updateHistoryNavigation();
                    }
                },
                error: () => {
                    this.isLoading = false;
                }
            });
    }

    submitAll(): void {
        const answers: ContextAnswer[] = this.pendingQuestions.map(q => {
            if (this.ignoredQuestions[q.id]) {
                return { questionId: q.id, answer: '[skipped]' };
            }
            const state = this.questionStates[q.id];
            let answer: string | string[] | null;
            switch (q.type) {
                case 'text':
                    answer = state.freeTextInput.trim() || null;
                    break;
                case 'text_array':
                    answer = state.chipsValue.length ? [...state.chipsValue] : (state.freeTextInput.trim() || null);
                    break;
                case 'multi_select':
                    answer = state.selectedOptions.length ? [...state.selectedOptions] : (state.freeTextInput.trim() || null);
                    break;
                case 'single_select':
                    answer = state.selectedOption ? [state.selectedOption] : (state.freeTextInput.trim() || null);
                    break;
            }
            return { questionId: q.id, answer };
        });

        this.conversationHistory.push({
            questions: this.pendingQuestions,
            answers
        });

        if (this.freeTextAnswer.trim()) {
            const additionalContextQuestion: ContextQuestion = {
                id: 'additional_context',
                type: 'text',
                question: 'Any additional context you like to add?'
            };
            this.conversationHistory.push({
                questions: [additionalContextQuestion],
                answers: [{ questionId: 'additional_context', answer: this.freeTextAnswer.trim() }]
            });
            this.freeTextAnswer = '';
        }

        this.pendingQuestions = [];
        this.questionStates = {};
        this.hasAnswers = false;
        this.callAgent();
    }

    useOriginalPurpose(): void {
        this.setBadgeState(this.badgeStateEnum.usingOriginalPurpose);
        this.refinedPurpose = this.originalPurpose;
    }

    setBadgeState(state: string = this.badgeStateEnum.generated): void {
        this.badgeText = this.badgeStateTextMap[state];
    }

    apply(): void {
        if (!this.applyFn) {
            this.closeDialog(this.refinedPurpose);
            return;
        }

        this.isApplying = true;
        this.agentSubscription?.unsubscribe();

        this.coreDialogService.open(ProgressDialogComponent, {
            inputs: {
                id: 'aiContextTakeoverProgress',
                alertMessage: 'Monitoring.search.prcopilot.generatingMessage',
                alertType: messageSeverityEnum.info,
                generatingText: 'Monitoring.search.prcopilot.generating',
                headerLabel: 'Monitoring.search.agilityAgent.contextTakeoverTitle',
                showCount: false,
                work$: this.applyFn(this.refinedPurpose),
                estimatedDurationMs: 8000 + Math.random() * 7000,
                progressStages: this.progressStages,
            },
            outputs: {
                dialogClose: (completed: boolean) => {
                    if (completed) {
                        this.closeDialog(this.refinedPurpose);
                    } else {
                        this.isApplying = false;
                    }
                }
            }
        });
    }

    previousQuestionState(): void {
        if (!this.canGoBack) {
            return;
        }
        this.historyIndex--;
        this.restoreHistoryEntry(this.questionHistory[this.historyIndex]);
    }

    nextQuestionState(): void {
        if (!this.canGoForward) {
            return;
        }
        this.historyIndex++;
        this.restoreHistoryEntry(this.questionHistory[this.historyIndex]);
    }

    private updateHistoryNavigation(): void {
        this.canGoBack = this.historyIndex > 0;
        this.canGoForward = this.historyIndex < this.questionHistory.length - 1;
    }

    private restoreHistoryEntry(entry: QuestionHistoryEntry): void {
        this.pendingQuestions = entry.pendingQuestions;
        this.questionStates = this.deepCopyQuestionStates(entry.questionStates);
        this.ignoredQuestions = {};
        this.refinedPurpose = entry.refinedPurpose;
        this.conversationHistory = [...entry.conversationHistory];
        this.freeTextAnswer = '';
        this.isComplete = entry.isComplete;
        this.badgeText = this.badgeStateTextMap[this.badgeStateEnum.generated];
        this.updateHistoryNavigation();
        this.updateHasAnswers();
    }

    private deepCopyQuestionStates(states: Record<string, QuestionState>): Record<string, QuestionState> {
        const copy: Record<string, QuestionState> = {};
        for (const [id, state] of Object.entries(states)) {
            copy[id] = {
                chipsValue: [...state.chipsValue],
                selectedOptions: [...state.selectedOptions],
                selectedOption: state.selectedOption,
                freeTextInput: state.freeTextInput,
            };
        }
        return copy;
    }

    cancel(): void {
        this.closeDialog(null);
    }

    handleVisibleChange(isVisible: boolean): void {
        if (!isVisible && !this.isApplying) {
            this.cancel();
        }
    }
}
